import { useCallback, useEffect, useState } from "react";
import { hasPermission, platformUser, PRODUCT_UPDATE } from "../../network/service/PlatformAuth";
import {
  createRecipe,
  deleteRecipe,
  listRecipes,
  searchPlatformProducts,
  updateRecipe,
} from "../../network/service/ShopService";
import PlatformSignInNotice from "../products/nutrition/platform-sign-in-notice";
import "../products/nutrition/nutrition-panel.css";

const EMPTY = {
  name: "",
  imagePath: "",
  imageCredit: "",
  cookMinutes: "",
  servings: "",
  description: "",
  ingredients: "",
  steps: "",
  products: [],
  isActive: true,
};

const lines = (text) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

/**
 * Recipe ideas shown on product screens (Recipes tab, nutrition screen).
 * Saved in the nutrition service; each recipe is linked to the products it
 * uses.
 */
function Recipes() {
  const user = platformUser();
  const canEdit = user && hasPermission(PRODUCT_UPDATE);
  const isAdmin = (user?.roles ?? []).some((r) => r === "SUPER_ADMIN" || r === "ADMIN");
  const allowed = canEdit || isAdmin;

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | recipe

  const load = useCallback(() => {
    if (!allowed) return;
    setError("");
    listRecipes({ q: q || undefined, page, limit: 20 })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [allowed, q, page]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <main className="container-fluid dashboard">
      <div className="nutri-panel">
        <div className="nutri-header">
          <div>
            <h4>Recipes</h4>
            <p className="nutri-muted mb-0">Recipe ideas customers see on the products they use.</p>
          </div>
          {allowed && !editing && (
            <button type="button" className="btn btn-purple-bg" onClick={() => setEditing("new")}>
              + New recipe
            </button>
          )}
        </div>

        {!user && <PlatformSignInNotice />}
        {user && !allowed && (
          <div className="nutri-status nutri-status-warn">Your role cannot edit recipes. Ask a super admin for catalog access.</div>
        )}

        {editing && (
          <RecipeForm
            recipe={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        )}

        {allowed && !editing && (
          <>
            <div className="nutri-row nutri-filters">
              <input
                className="form-control"
                placeholder="Search recipes"
                aria-label="Search recipes"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            {error && (
              <div className="alert alert-danger">
                {error}{" "}
                <button type="button" className="nutri-link" onClick={load}>
                  Retry
                </button>
              </div>
            )}
            <table className="table table-sm nutri-worklist">
              <thead>
                <tr>
                  <th>Recipe</th>
                  <th>Time</th>
                  <th>Products</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!data && !error && (
                  <tr>
                    <td colSpan={5} className="nutri-muted">Loading…</td>
                  </tr>
                )}
                {data?.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="nutri-muted">No recipes yet. Add the first one.</td>
                  </tr>
                )}
                {data?.items.map((recipe) => (
                  <tr key={recipe.id}>
                    <td>
                      <strong>{recipe.name}</strong>
                      <div className="nutri-muted">#{recipe.id}</div>
                    </td>
                    <td>{recipe.cookMinutes} mins</td>
                    <td>{recipe.products.map((p) => p.name ?? `#${p.id}`).join(", ")}</td>
                    <td>{recipe.isActive ? "Shown" : "Hidden"}</td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-purple-bg" onClick={() => setEditing(recipe)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.totalPages > 1 && (
              <div className="nutri-actions">
                <button type="button" className="btn btn-white-bg" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <span className="nutri-muted">
                  Page {page} of {data.totalPages}
                </span>
                <button type="button" className="btn btn-white-bg" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function RecipeForm({ recipe, onCancel, onSaved }) {
  const [form, setForm] = useState(
    recipe
      ? {
          name: recipe.name,
          imagePath: recipe.imagePath ?? "",
          imageCredit: recipe.imageCredit ?? "",
          cookMinutes: String(recipe.cookMinutes),
          servings: recipe.servings ? String(recipe.servings) : "",
          description: recipe.description ?? "",
          ingredients: recipe.ingredients.join("\n"),
          steps: recipe.steps.join("\n"),
          products: recipe.products,
          isActive: recipe.isActive,
        }
      : EMPTY,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !(Number(form.cookMinutes) > 0) || !form.products.length) {
      setError("Enter the name, cooking time and at least one product.");
      return;
    }
    const body = {
      name: form.name.trim(),
      imagePath: form.imagePath.trim() || undefined,
      imageCredit: form.imageCredit.trim() || undefined,
      cookMinutes: Number(form.cookMinutes),
      servings: Number(form.servings) > 0 ? Number(form.servings) : undefined,
      description: form.description.trim() || undefined,
      ingredients: lines(form.ingredients),
      steps: lines(form.steps),
      productIds: form.products.map((p) => p.id),
      isActive: form.isActive,
    };
    setSaving(true);
    try {
      if (recipe) await updateRecipe(recipe.id, body);
      else await createRecipe(body);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete “${recipe.name}”? It will disappear from every product.`)) return;
    setSaving(true);
    try {
      await deleteRecipe(recipe.id);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form className="nutri-section" onSubmit={save} style={{ maxWidth: 820 }}>
      <h5>{recipe ? "Edit recipe" : "New recipe"}</h5>
      <div className="row g-3">
        <div className="col-md-8">
          <label className="form-label" htmlFor="recipe-name">Name</label>
          <input id="recipe-name" className="form-control" value={form.name} onChange={set("name")} maxLength={200} required />
        </div>
        <div className="col-md-2">
          <label className="form-label" htmlFor="recipe-time">Minutes</label>
          <input id="recipe-time" className="form-control" type="number" min={1} max={1440} value={form.cookMinutes} onChange={set("cookMinutes")} required />
        </div>
        <div className="col-md-2">
          <label className="form-label" htmlFor="recipe-serves">Serves</label>
          <input id="recipe-serves" className="form-control" type="number" min={1} max={100} value={form.servings} onChange={set("servings")} />
        </div>
        <div className="col-md-7">
          <label className="form-label" htmlFor="recipe-image">Image URL</label>
          <input id="recipe-image" className="form-control" value={form.imagePath} onChange={set("imagePath")} placeholder="https://…" />
        </div>
        <div className="col-md-5">
          <label className="form-label" htmlFor="recipe-credit">Image credit</label>
          <input
            id="recipe-credit"
            className="form-control"
            value={form.imageCredit}
            onChange={set("imageCredit")}
            maxLength={300}
            placeholder="Photo: name, licence (if not your own photo)"
          />
        </div>
        <div className="col-12">
          <label className="form-label" htmlFor="recipe-description">Description</label>
          <textarea id="recipe-description" className="form-control" rows={2} value={form.description} onChange={set("description")} maxLength={2000} />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="recipe-ingredients">Ingredients (one per line)</label>
          <textarea id="recipe-ingredients" className="form-control" rows={7} value={form.ingredients} onChange={set("ingredients")} />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="recipe-steps">Method (one step per line)</label>
          <textarea id="recipe-steps" className="form-control" rows={7} value={form.steps} onChange={set("steps")} />
        </div>
        <div className="col-12">
          <ProductPicker value={form.products} onChange={(products) => setForm({ ...form, products })} />
        </div>
        <div className="col-12 form-check ms-2">
          <input id="recipe-active" className="form-check-input" type="checkbox" checked={form.isActive} onChange={set("isActive")} />
          <label className="form-check-label" htmlFor="recipe-active">Show to customers</label>
        </div>
      </div>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <div className="nutri-actions mt-3">
        <button type="submit" className="btn btn-purple-bg" disabled={saving}>
          {saving ? "Saving…" : "Save recipe"}
        </button>
        <button type="button" className="btn btn-white-bg" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        {recipe && (
          <button type="button" className="btn btn-outline-danger ms-auto" onClick={remove} disabled={saving}>
            Delete
          </button>
        )}
      </div>
    </form>
  );
}

/** Search the platform catalogue and link products to the recipe. */
function ProductPicker({ value, onChange }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (text.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      searchPlatformProducts(text.trim())
        .then((page) => alive && setResults(page.items))
        .catch(() => alive && setResults([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [text]);

  return (
    <div>
      <label className="form-label" htmlFor="recipe-products">Shown on products</label>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {value.map((p) => (
          <span key={p.id} className="badge rounded-pill text-bg-light border" style={{ fontSize: 13, fontWeight: 500 }}>
            {p.name ?? `#${p.id}`}{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              aria-label={`Unlink ${p.name ?? p.id}`}
              onClick={() => onChange(value.filter((x) => x.id !== p.id))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        id="recipe-products"
        className="form-control"
        placeholder="Type a product name, e.g. toor dal"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {results.length > 0 && (
        <div className="list-group mt-1" style={{ maxHeight: 220, overflowY: "auto" }}>
          {results
            .filter((p) => !value.some((x) => x.id === p.id))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="list-group-item list-group-item-action"
                onClick={() => {
                  onChange([...value, { id: p.id, name: p.name }]);
                  setText("");
                }}
              >
                {p.name} <span className="nutri-muted">#{p.id}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default Recipes;
